import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useEffect } from "react";

import { Field } from "../field";

import type { IFastMultiSelectProps } from "./types";
import { dropdownIcons } from "./icon";
import { isEmptyObject, useMultiselectQueryParams } from "./helper";

import { Multiselect } from "@/components/formControls";
import { useOptions } from "@/api/dropdowns";
import type { IOption } from "@/interfaces";
import type { ID } from "@/constants";
import { useReadOnly } from "@/hooks";

export const FastMultiSelect = memo(function FastMultiSelect<T>(
  props: IFastMultiSelectProps<T>,
) {
  const {
    formik,
    name,
    icon,
    title,
    placeholder,
    readOnly: propsReadOnly,
    dependencies,
    disabled: propsDisabled,
    onInputChange,
    params,
    ...rest
  } = props;
  const readOnly = useReadOnly(propsReadOnly);

  const { t } = useTranslation("form");
  const { type } = rest;
  const defaultIcon = dropdownIcons[type];
  const { values, setFieldValue, errors, touched, handleBlur } = formik;

  const {
    params: queryParam,
    state,
    isChanged,
  } = useMultiselectQueryParams(values, { ...rest });

  useEffect(() => {
    if (isChanged) {
      setFieldValue(name, []);
    }
  }, [state, isChanged, setFieldValue, name]);

  // queryParam.params!==undefined => because we do not want to check the undefined value.
  // in `allDepartments` it should be undefined
  const disabled =
    propsDisabled ||
    (queryParam.params !== undefined && isEmptyObject(queryParam.params));

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useOptions({
    variables: queryParam,
    enabled: !readOnly && !disabled,
    staleTime: type === "allDepartments" ? Infinity : undefined,
  });

  // const queryVariables: QueryVariablesType = {
  //   optionType: type,
  //   params: undefined
  // }
  // const { data, isLoading, isError } = useOptions({
  //   variables: queryVariables,
  //   enabled: !readOnly
  // })

  const options = data ? data.data : [];
  const _title = title || type;

  function handleChange(value: IOption<ID>[]) {
    setFieldValue(name, value);
    if (dependencies) {
      for (const dependency of dependencies) {
        setFieldValue(dependency, { key: "", value: "" });
      }
    }
  }

  return (
    <Field
      name={name}
      title={_title}
      formik={props.formik}
      icon={icon || defaultIcon}
      isRequired={props.isRequired && !readOnly}
    >
      <Multiselect
        isLoading={isLoading}
        isError={isError}
        placeholder={placeholder || t(_title)}
        id={name.toString()}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values[name] as IOption[]}
        options={options}
        validation={touched[name] && errors[name] ? "error" : undefined}
        readOnly={readOnly}
        disabled={disabled}
      />
    </Field>
  );
}) as unknown as <T>(props: IFastMultiSelectProps<T>) => JSX.Element;
